/// <amd-module name="VSS/Platform/Security" />
import { IVssService, Services, VssService } from "./Context";
import { IVssContributionService } from "./Contribution";

/**
 * Service for obtaining security permission states populated by IClientSecurityProviderService.
 */
export interface IVssSecurityService extends IVssService {
    /**
     * Determines whether the current user has the requested permission on the provided token using the data
     * sent back to the client through IClientSecurityProviderService (within a data provider or request handler).
     *
     * @param securityNamespaceId Id of the security namespace.
     * @param securityToken The token on which to test permissions.
     * @param requestedPermission Permission value to check.
     */
    hasPermission(securityNamespaceId: string, securityToken: string, requestedPermission: number): boolean;

    /**
     * Throws an access denied exception if the current user does not have the requested permissions on the given
     * security namespace and the token using IClientSecurityProviderService (within a data provider or request handler).
     *
     * @param securityNamespaceId Id of the security namespace.
     * @param securityToken The token on which to test permissions.
     * @param requestedPermission Permission value to check.
     */
    checkPermission(securityNamespaceId: string, securityToken: string, requestedPermission: number): void;
}

interface SharedPermissionCollection {
    [namespaceId: string]: SharedPermissionData;
}

interface SharedPermissionData {
    [token: string]: number;
}

const permissionsSharedDataKey = "_permissions";

class VssSecurityService extends VssService implements IVssSecurityService {
    public hasPermission(securityNamespaceId: string, securityToken: string, requestedPermission: number): boolean {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const sharedPermissions = contributionService.getSharedData<SharedPermissionCollection>(permissionsSharedDataKey);
        if (!sharedPermissions) {
            return this.handleMissingState(`No shared permissions found`);
        }

        const tokens = sharedPermissions[securityNamespaceId.toLowerCase()];
        if (!tokens) {
            return this.handleMissingState(`Security namespace with id '${securityNamespaceId}' not found`);
        }

        const effectivePermissions = tokens[securityToken];
        if (effectivePermissions === undefined) {
            return this.handleMissingState(`No permissions found for security namespace '${securityNamespaceId}' and token '${securityToken}'`);
        }

        return (requestedPermission & effectivePermissions) > 0;
    }

    public checkPermission(securityNamespaceId: string, securityToken: string, requestedPermission: number): void {
        if (!this.hasPermission(securityNamespaceId, securityToken, requestedPermission)) {
            throw new Error(`Access denied: Permission ${requestedPermission} is needed for the resource ${securityToken}.`);
        }
    }

    private handleMissingState(message: string): boolean {
        console.error(`${message}. Ensure that the effective permissions are included in shared data.`);
        return false;
    }
}

Services.add("IVssSecurityService", { serviceFactory: VssSecurityService });
